(function () {
  const STORAGE_KEY = "ring-sizer-calibration";
  const SELECT_EVENT = "ring-sizer:select";
  // Bank and ID cards are ISO/IEC 7810 ID-1: 85.60 x 53.98 mm. The short edge fits upright on a phone screen.
  const CARD_WIDTH_MM = 53.98;
  const CARD_VISIBLE_HEIGHT_MM = 36;
  const DEFAULT_PX_PER_MM = 96 / 25.4;
  const NUDGE_PX_PER_MM = 0.02;
  // Measurements within this many mm of a size count as that size; anything above rounds up.
  // Circumference and diameter tolerances match (0.5 mm around ≈ 0.15 mm across).
  const TOLERANCE_MM = { circumference: 0.5, diameter: 0.15 };

  const readJson = (root, selector) => {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || "null");
    } catch (e) {
      return null;
    }
  };

  // Calibration only holds for the screen it was made on, so tie it to the screen size and zoom level.
  const screenSignature = () => {
    const short = Math.min(window.screen.width, window.screen.height);
    const long = Math.max(window.screen.width, window.screen.height);
    return `${short}x${long}@${window.devicePixelRatio}`;
  };

  const loadCalibration = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved && saved.signature === screenSignature() && saved.pxPerMm > 0) {
        return saved.pxPerMm;
      }
    } catch (e) {}
    return null;
  };

  const saveCalibration = (pxPerMm) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ pxPerMm, signature: screenSignature() })
      );
    } catch (e) {}
  };

  // Translations arrive HTML-escaped from the t filter; decode them for use as plain text.
  const decodeStrings = (strings) =>
    Object.fromEntries(
      Object.entries(strings || {}).map(([key, value]) => [
        key,
        new DOMParser().parseFromString(value, "text/html").documentElement.textContent,
      ])
    );

  const diameterOf = (size) => size.dia;

  const fillTemplate = (template, size) =>
    template
      .replace("[eu]", size.eu)
      .replace("[us]", size.us)
      .replace("[uk]", size.uk)
      .replace("[jp]", size.jp);

  if (!customElements.get("ring-sizer")) {
    class RingSizer extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.sizes = readJson(this, "[data-ring-sizes]") || [];
        if (this.sizes.length === 0) return;
        this.initialized = true;

        this.panels = {
          calibrate: this.querySelector('[data-panel="calibrate"]'),
          measure: this.querySelector('[data-panel="measure"]'),
        };
        this.card = this.querySelector("[data-card]");
        this.circle = this.querySelector("[data-circle]");
        this.current = this.querySelector("[data-current]");
        this.scaleRange = this.querySelector("[data-scale-range]");
        this.sizeRange = this.querySelector("[data-size-range]");
        this.rows = Array.from(this.querySelectorAll("[data-size-row]"));

        this.pxPerMm = loadCalibration();
        this.index = Number(this.dataset.defaultIndex) || 0;

        this.scaleRange.addEventListener("input", () =>
          this.setScale(parseFloat(this.scaleRange.value))
        );
        this.querySelectorAll("[data-nudge]").forEach((button) =>
          button.addEventListener("click", () =>
            this.setScale(this.scale + Number(button.dataset.nudge) * NUDGE_PX_PER_MM)
          )
        );
        this.querySelector("[data-calibration-done]").addEventListener("click", () => {
          this.pxPerMm = this.scale;
          saveCalibration(this.pxPerMm);
          this.showPanel("measure");
        });
        this.querySelector("[data-recalibrate]").addEventListener("click", () =>
          this.showPanel("calibrate")
        );

        this.sizeRange.addEventListener("input", () =>
          this.setIndex(parseInt(this.sizeRange.value, 10))
        );
        this.querySelectorAll("[data-size-step]").forEach((button) =>
          button.addEventListener("click", () =>
            this.setIndex(this.index + Number(button.dataset.sizeStep))
          )
        );
        this.rows.forEach((row, index) =>
          row.addEventListener("click", () => this.setIndex(index))
        );

        this.onSelect = (event) => this.setIndex(event.detail.index);
        document.addEventListener(SELECT_EVENT, this.onSelect);

        this.setScale(this.pxPerMm || DEFAULT_PX_PER_MM);
        this.showPanel(this.pxPerMm ? "measure" : "calibrate");
      }

      disconnectedCallback() {
        if (this.onSelect) document.removeEventListener(SELECT_EVENT, this.onSelect);
      }

      setScale(value) {
        const min = parseFloat(this.scaleRange.min);
        const max = parseFloat(this.scaleRange.max);
        this.scale = Math.min(Math.max(value, min), max);
        this.scaleRange.value = this.scale;
        this.card.style.width = `${(CARD_WIDTH_MM * this.scale).toFixed(2)}px`;
        this.card.style.height = `${(CARD_VISIBLE_HEIGHT_MM * this.scale).toFixed(2)}px`;
      }

      setIndex(value) {
        this.index = Math.min(Math.max(value, 0), this.sizes.length - 1);
        this.sizeRange.value = this.index;

        const size = this.sizes[this.index];
        const diameter = diameterOf(size);
        // content-box sizing keeps the inside edge of the outline at the true inner diameter.
        const diameterPx = `${(diameter * (this.pxPerMm || this.scale)).toFixed(2)}px`;
        this.circle.style.width = diameterPx;
        this.circle.style.height = diameterPx;
        this.current.textContent = `EU ${size.eu} · US ${size.us} · UK ${size.uk} · JP ${size.jp} · Ø ${diameter.toFixed(1)} mm`;

        this.rows.forEach((row, index) =>
          row.classList.toggle("is-active", index === this.index)
        );
      }

      showPanel(name) {
        Object.entries(this.panels).forEach(([key, panel]) => {
          panel.hidden = key !== name;
        });
        this.querySelectorAll("[data-hint]").forEach((hint) => {
          hint.setAttribute("aria-hidden", hint.dataset.hint === name ? "false" : "true");
        });
        if (name === "measure") this.setIndex(this.index);
      }
    }

    customElements.define("ring-sizer", RingSizer);
  }

  if (!customElements.get("ring-size-finder")) {
    class RingSizeFinder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.sizes = readJson(this, "[data-ring-sizes]") || [];
        this.strings = decodeStrings(readJson(this, "[data-ring-strings]"));
        this.form = this.querySelector("[data-finder-form]");
        this.input = this.querySelector("[data-finder-input]");
        this.result = this.querySelector("[data-finder-result]");
        if (this.sizes.length === 0 || !this.form) return;
        this.initialized = true;

        this.form.addEventListener("submit", (event) => {
          event.preventDefault();
          this.find();
        });

        // Each measurement type has its own example value as placeholder.
        this.form.querySelectorAll('input[name="ring-sizer-mode"]').forEach((radio) =>
          radio.addEventListener("change", () => {
            if (radio.checked && radio.dataset.placeholder) {
              this.input.placeholder = radio.dataset.placeholder;
            }
          })
        );
      }

      find() {
        const value = parseFloat(this.input.value.trim().replace(",", "."));
        const mode = this.form.querySelector('input[name="ring-sizer-mode"]:checked')?.value;

        if (!Number.isFinite(value) || value <= 0) {
          return this.show(this.strings.invalid, true);
        }
        if (mode === "diameter" && value > 30) {
          return this.show(this.strings.looks_like_circumference, true);
        }
        if (mode === "circumference" && value < 30) {
          return this.show(this.strings.looks_like_diameter, true);
        }

        // Compare in the unit the customer measured: diameter against the chart's diameters, circumference against EU sizes.
        const sizeValue = (size) => (mode === "diameter" ? size.dia : size.eu);
        const tolerance = TOLERANCE_MM[mode];
        const smallest = this.sizes[0];
        const largest = this.sizes[this.sizes.length - 1];

        if (value < sizeValue(smallest) - tolerance * 2) {
          return this.show(fillTemplate(this.strings.too_small, smallest), true);
        }
        if (value > sizeValue(largest) + tolerance) {
          return this.show(fillTemplate(this.strings.too_large, largest), true);
        }

        const index = this.sizes.findIndex((size) => sizeValue(size) >= value - tolerance);
        const size = this.sizes[index];
        let message = fillTemplate(this.strings.result, size);
        if (sizeValue(size) - value > tolerance) {
          message += ` ${this.strings.rounded_up}`;
        }

        this.show(message, false);
        document.dispatchEvent(new CustomEvent(SELECT_EVENT, { detail: { index } }));
      }

      show(message, isError) {
        this.result.textContent = message || "";
        this.result.classList.toggle("ring-sizer__result--error", isError);
      }
    }

    customElements.define("ring-size-finder", RingSizeFinder);
  }
})();
